import { BookingType, EmailAttachmentInfo } from '../models/index';
import { ApiClient } from './api-client.service';

// ──────────────────────────────────────────────────────────────
// Gemini Booking Parser — parses booking confirmation emails
// using Gemini AI. Mirrors the mobile app GeminiBookingParser.
// Uses type-specific prompts (accommodation/flight/car-rental).
// ──────────────────────────────────────────────────────────────

export interface ParsedBookingData {
    bookingType: BookingType;
    propertyName?: string;
    propertyAddress?: string;
    city?: string;
    country?: string;
    airline?: string;
    checkInDate?: string; // ISO date string (kept as string for Firestore)
    checkOutDate?: string;
    totalPrice?: number;
    currency?: string;
    confirmationNumber?: string;
    vendor?: string;
    guestName?: string;
    // Flight-specific
    flightNumber?: string;
    fromLocation?: string;
    toLocation?: string;
    departureTime?: string;
    arrivalTime?: string;
    segments?: Array<{
        airline?: string;
        flightNumber?: string;
        fromLocation?: string;
        toLocation?: string;
        departureTime?: string;
        arrivalTime?: string;
        gate?: string;
        terminal?: string;
        seat?: string;
    }>;
    // Car Rental-specific
    vehicleType?: string;
    pickupLocation?: string;
    returnLocation?: string;
    pickupDate?: string;
    returnDate?: string;
    company?: string;
    driverName?: string;
    // Accommodation cancellation
    cancellationDeadline?: string; // ISO date string
    freeCancellation?: boolean;
    // Confidence
    confidence?: number;
    // Source-email linkage (populated after parsing, not by the AI). Lets
    // created entities (e.g. flights) link back to the original email/attachments.
    sourceEmailId?: string;
    attachmentInfos?: EmailAttachmentInfo[];
}

export class GeminiBookingParser {
    private async generateText(prompt: string): Promise<string> {
        const data = await ApiClient.post<{ result: string }>('/ai/generate', {
            prompt,
            config: { temperature: 0.3, maxTokens: 4096 },
        });
        return data.result ?? '';
    }

    /**
     * Multimodal generation: send the prompt together with an inline
     * attachment (a forwarded e-ticket/voucher PDF or an image). Gemini's
     * inlineData reads application/pdf as well as images, so this recovers
     * booking fields (price, times, PNR…) that live only in the document and
     * are absent from the email body. Falls back to text-only on the caller
     * side if no attachment is available.
     */
    private async generateWithAttachment(
        prompt: string,
        base64: string,
        mimeType: string
    ): Promise<string> {
        const data = await ApiClient.post<{ result: string }>('/ai/generate-with-image', {
            prompt,
            imageBase64: base64,
            mimeType,
            // thinkingBudget:0 → give the whole budget to the JSON output so
            // multi-segment flight results aren't truncated (see server note).
            config: { temperature: 0.1, maxTokens: 4096, thinkingBudget: 0 },
        });
        return data.result ?? '';
    }

    /**
     * Detect booking type from email content before full parsing.
     */
    detectBookingType(subject: string, body: string, sender: string): BookingType {
        const combined = `${subject} ${body} ${sender}`.toLowerCase();

        const flightKeywords = ['flight', 'airline', 'boarding pass', 'departure', 'arrival',
            'terminal', 'gate', 'seat', 'e-ticket', 'itinerary',
            'el al', 'ryanair', 'easyjet', 'lufthansa', 'british airways',
            'wizz air', 'turkish airlines', 'united airlines', 'delta',
            'american airlines', 'southwest', 'qatar', 'emirates'];

        const carKeywords = ['car rental', 'rental car', 'vehicle', 'pick-up', 'drop-off',
            'hertz', 'avis', 'sixt', 'europcar', 'enterprise', 'budget',
            'national', 'alamo', 'thrifty', 'dollar', 'sunnycars',
            'green motion', 'goldcar'];

        const hotelKeywords = ['hotel', 'accommodation', 'check-in', 'check-out', 'room',
            'booking.com', 'expedia', 'hotels.com', 'airbnb', 'hostel',
            'apartment', 'resort', 'villa', 'guesthouse', 'campsite',
            'agoda', 'trivago'];

        // Activities / attractions / tours / tickets — including events, concerts
        // and sports tickets. Event tickets are tricky: their venue "seat"/"row"
        // wording overlaps flight keywords and stray body text can hit a hotel
        // keyword, so the activity vocabulary must be rich enough to outscore them.
        const activityKeywords = ['activity', 'attraction', 'tour', 'guided tour', 'museum',
            'admission', 'entrance ticket', 'entry ticket', 'skip the line', 'skip-the-line',
            'excursion', 'experience', 'sightseeing', 'theme park', 'day trip',
            // Events / concerts / shows / sports
            'event', 'concert', 'festival', 'show', 'match', 'game', 'sporting', 'sports',
            'stadium', 'arena', 'venue', 'box office', 'ticket', 'tickets', 'seat', 'row',
            // Activity & event ticketing platforms
            'getyourguide', 'viator', 'tiqets', 'klook', 'headout', 'musement',
            'ticketmaster', 'ticketone', 'eventim', 'ticketek', 'stubhub', 'seatgeek',
            'axs', 'dice.fm', 'live nation', 'livenation', 'fansale', 'vivaticket'];

        const flightScore = flightKeywords.filter(k => combined.includes(k)).length;
        const carScore = carKeywords.filter(k => combined.includes(k)).length;
        const hotelScore = hotelKeywords.filter(k => combined.includes(k)).length;
        const activityScore = activityKeywords.filter(k => combined.includes(k)).length;

        // Pick the category with the strongest keyword signal. Ties are broken by
        // the order below (flight > car > hotel > activity) so a stronger, more
        // specific travel signal still wins first — but a richer activity/event
        // signal is no longer pre-empted by a single stray hotel/flight keyword.
        const ranked: Array<[BookingType, number]> = [
            [BookingType.FLIGHT, flightScore],
            [BookingType.CAR_RENTAL, carScore],
            [BookingType.ACCOMMODATION, hotelScore],
            [BookingType.ACTIVITY, activityScore],
        ];
        const [bestType, bestScore] = ranked.reduce((a, b) => (b[1] > a[1] ? b : a));
        return bestScore > 0 ? bestType : BookingType.UNKNOWN;
    }

    /**
     * Parse booking email content using Gemini AI.
     * Returns null if the email is not a booking or parsing fails.
     * Retries up to 2 times on failure.
     */
    async parseBookingEmail(
        emailSubject: string,
        emailBody: string,
        emailSender: string,
        attachment?: { base64: string; mimeType: string }
    ): Promise<ParsedBookingData | null> {
        const maxRetries = 2;

        for (let attempt = 0; attempt <= maxRetries; attempt++) {
            try {
                const detectedType = this.detectBookingType(emailSubject, emailBody, emailSender);

                const prompt = this.buildPromptForType(detectedType, emailSubject, emailBody);

                // When the forward carries a PDF/image document (e-ticket,
                // voucher), send it inline so the model reads fields missing
                // from the body text (e.g. the fare that lives only in the PDF).
                const run = (p: string) => attachment
                    ? this.generateWithAttachment(p, attachment.base64, attachment.mimeType)
                    : this.generateText(p);

                const text = await run(prompt);

                // Extract JSON from response
                const jsonMatch = text.match(/\{[\s\S]*\}/);
                if (!jsonMatch) {
                    console.warn(`No JSON in Gemini response (attempt ${attempt + 1})`);
                    if (attempt < maxRetries) {
                        await new Promise(r => setTimeout(r, 1000));
                        continue;
                    }
                    return null;
                }

                let parsed = JSON.parse(jsonMatch[0]);
                let effectiveType = detectedType;

                // The prompt is picked from keyword scoring, which regularly
                // guesses wrong (a hotel voucher full of "check-in"/"e-ticket"/
                // "seat" wording scores as a flight). When the model disagrees
                // with that guess, its verdict wins — but the first answer was
                // extracted against the WRONG schema, so re-ask with the right
                // type-specific prompt instead of keeping half-empty fields.
                const declared = GeminiBookingParser.readDeclaredType(parsed);
                if (declared === BookingType.UNRECOGNIZED && declared !== detectedType) {
                    // The model read the whole mail (and any voucher) and says
                    // this isn't a travel booking it can classify. Trust that
                    // over the keyword guess: it is surfaced for review instead
                    // of being auto-added to a trip under the wrong type. No
                    // re-parse — the generic schema adds nothing here.
                    console.log(
                        `[GeminiParser] Model rejected the detected "${detectedType}" type — keeping it unrecognized`
                    );
                    effectiveType = BookingType.UNRECOGNIZED;
                } else if (declared && declared !== detectedType && declared !== BookingType.UNRECOGNIZED) {
                    console.log(
                        `[GeminiParser] Type mismatch: detected "${detectedType}" but model says ` +
                        `"${parsed.bookingType ?? parsed.detectedType}" — re-parsing with the ${declared} prompt`
                    );
                    try {
                        const retryText = await run(
                            this.buildPromptForType(declared, emailSubject, emailBody)
                        );
                        const retryMatch = retryText.match(/\{[\s\S]*\}/);
                        if (retryMatch) {
                            parsed = JSON.parse(retryMatch[0]);
                            // Keep the corrected type even if the second pass
                            // omits/renames it in its own response.
                            if (!GeminiBookingParser.mapDeclaredType(parsed.bookingType)) {
                                parsed.bookingType = declared;
                            }
                        }
                    } catch (retryError) {
                        console.warn('[GeminiParser] Re-parse after type mismatch failed:', retryError);
                    }
                    // Fall back to the model's own type rather than the
                    // keyword guess, whether or not the re-parse succeeded.
                    effectiveType = declared;
                }

                // 🔍 Log raw Gemini fields before normalisation
                console.log('[GeminiParser] Raw Gemini response fields:', {
                    bookingType: parsed.bookingType,
                    totalPrice: parsed.totalPrice,
                    currency: parsed.currency,
                    segments: Array.isArray(parsed.segments) ? parsed.segments.length : undefined,
                    freeCancellation: parsed.freeCancellation,
                    cancellationDeadline: parsed.cancellationDeadline,
                    cancelUntil: parsed.cancelUntil,
                    propertyName: parsed.propertyName,
                    checkInDate: parsed.checkInDate,
                });

                // Do NOT drop unclear results: they're surfaced as
                // unrecognized (never auto-added to a trip) so no forwarded
                // mail is ever missed. normalizeBookingData maps the type.
                const normalized = this.normalizeBookingData(parsed, effectiveType);
                console.log('[GeminiParser] Normalised result fields:', {
                    bookingType: normalized.bookingType,
                    totalPrice: normalized.totalPrice,
                    currency: normalized.currency,
                    freeCancellation: normalized.freeCancellation,
                    cancellationDeadline: normalized.cancellationDeadline,
                });
                return normalized;
            } catch (error) {
                console.error(`Error parsing booking email (attempt ${attempt + 1}):`, error);
                if (attempt < maxRetries) {
                    await new Promise(r => setTimeout(r, 1500));
                    continue;
                }
                return null;
            }
        }

        return null;
    }

    /**
     * Map the `bookingType` string the model returned onto our enum.
     * Gemini frequently answers with a synonym instead of the exact enum
     * value ("hotel" for accommodation, "car" for carRental…), so match
     * loosely. Returns null when the value is missing/unmappable, letting the
     * caller fall back to the keyword-detected type.
     */
    private static mapDeclaredType(raw: any): BookingType | null {
        const typeStr = (typeof raw === 'string' ? raw : '').toLowerCase().replace(/[\s_-]/g, '');
        if (!typeStr) return null;
        if (typeStr === 'flight' || typeStr === 'air' || typeStr === 'airfare') {
            return BookingType.FLIGHT;
        }
        if (typeStr === 'carrental' || typeStr === 'car' || typeStr === 'rentalcar' || typeStr === 'vehicle') {
            return BookingType.CAR_RENTAL;
        }
        if (
            typeStr === 'accommodation' || typeStr === 'hotel' || typeStr === 'lodging' ||
            typeStr === 'apartment' || typeStr === 'hostel' || typeStr === 'stay' ||
            typeStr === 'guesthouse' || typeStr === 'bnb' || typeStr === 'airbnb'
        ) {
            return BookingType.ACCOMMODATION;
        }
        if (
            typeStr === 'activity' || typeStr === 'tour' || typeStr === 'attraction' ||
            typeStr === 'event' || typeStr === 'ticket' || typeStr === 'experience' ||
            typeStr === 'tourpackage' || typeStr === 'package' || typeStr === 'excursion' ||
            typeStr === 'sightseeing' || typeStr === 'daytrip' || typeStr === 'attractionticket'
        ) {
            return BookingType.ACTIVITY;
        }
        if (typeStr === 'unknown' || typeStr === 'unrecognized' || typeStr === 'other') {
            return BookingType.UNRECOGNIZED;
        }
        return null;
    }

    /**
     * Read the type the model declared in its answer. Typed prompts return it
     * as `bookingType`; the generic prompt always answers `bookingType:
     * "unknown"` and puts its real verdict in `detectedType`, so that field is
     * consulted whenever `bookingType` is missing or non-committal — otherwise
     * a generic parse that DID recognise the mail stayed unrecognized forever.
     * (Mirrors the mobile parser's _declaredTypeOf.)
     */
    private static readDeclaredType(parsed: any): BookingType | null {
        const fromBookingType = GeminiBookingParser.mapDeclaredType(parsed?.bookingType);
        if (fromBookingType && fromBookingType !== BookingType.UNRECOGNIZED) {
            return fromBookingType;
        }
        return GeminiBookingParser.mapDeclaredType(parsed?.detectedType) ?? fromBookingType;
    }

    /** Pick the type-specific extraction prompt (generic when unclear). */
    private buildPromptForType(type: BookingType, subject: string, body: string): string {
        switch (type) {
            case BookingType.FLIGHT:
                return this.buildFlightPrompt(subject, body);
            case BookingType.CAR_RENTAL:
                return this.buildCarRentalPrompt(subject, body);
            case BookingType.ACTIVITY:
                return this.buildActivityPrompt(subject, body);
            case BookingType.ACCOMMODATION:
                return this.buildAccommodationPrompt(subject, body);
            default:
                // Unclear type — best-effort generic extraction so the mail is
                // still brought in (kept unrecognized, not auto-added to a trip).
                return this.buildGenericPrompt(subject, body);
        }
    }

    // ─── Specialized prompts for each booking type ───────────────

    // Max email body chars sent to the model. Kept generous because the
    // grand-total / price line frequently sits at the very BOTTOM of a long
    // breakdown table — an aggressive cut silently drops totalPrice.
    private static readonly MAX_BODY_CHARS = 24000;

    /**
     * The type-specific prompts below are chosen by keyword scoring BEFORE
     * anything reads the mail, and that guess is regularly wrong (a tour
     * operator's confirmation full of "itinerary"/"departure"/"passenger"
     * wording scores as a flight). Because each schema hard-codes its own
     * bookingType, the model had no way to object and just echoed the wrong
     * type back — which is why the "model verdict wins" recovery in
     * parseBookingEmail never fired. This note is that escape hatch.
     */
    private static readonly RECLASSIFY_NOTE = `- IMPORTANT — the "bookingType" in the structure above is only a guess made before anyone read this email. If the email is actually a different kind of booking, put the CORRECT value in "bookingType" instead: one of accommodation, flight, carRental, activity, or "unknown" when it is not a travel booking at all. Judge by what was actually reserved, not by incidental mentions: a tour/package/hotel confirmation that merely lists flight times, transfers or an "arrival"/"departure" is NOT a flight booking, and a tour, excursion, day trip or sightseeing package sold by a travel agency is "activity". When you change the type, fill in whatever fields you can and leave the rest null — the email will be re-parsed with the correct schema`;

    private truncateBody(body: string): string {
        return body.length > GeminiBookingParser.MAX_BODY_CHARS
            ? body.substring(0, GeminiBookingParser.MAX_BODY_CHARS) + '\n...[truncated]'
            : body;
    }

    private buildAccommodationPrompt(subject: string, body: string): string {
        const truncatedBody = this.truncateBody(body);

        return `You are an AI that extracts hotel/accommodation booking information from emails.

Email Subject: ${subject}

Email Content:
${truncatedBody}

Extract booking information. Return ONLY a valid JSON object (no markdown, no explanation):

{
  "bookingType": "accommodation",
  "propertyName": "exact hotel/property name",
  "propertyAddress": "full address if available",
  "city": "city name",
  "country": "country name",
  "checkInDate": "YYYY-MM-DD",
  "checkOutDate": "YYYY-MM-DD",
  "totalPrice": numeric value only,
  "currency": "USD/EUR/ILS/etc",
  "confirmationNumber": "booking confirmation number",
  "vendor": "booking platform (booking.com, expedia, airbnb, etc.)",
  "guestName": "guest name if available",
  "freeCancellation": true or false,
  "cancellationDeadline": "YYYY-MM-DDTHH:MM:SS or YYYY-MM-DD if free cancellation deadline is mentioned, otherwise null",
  "confidence": 0.0 to 1.0
}

Rules:
- Handle Hebrew, English, and other languages
- If a field is not found, use null
- Be precise with property names and addresses
- freeCancellation is true if the email mentions free cancellation, refundable, or a cancel-by date
${GeminiBookingParser.RECLASSIFY_NOTE}
- Return ONLY the JSON object`;
    }

    private buildFlightPrompt(subject: string, body: string): string {
        const truncatedBody = this.truncateBody(body);

        return `You are an AI that extracts flight booking information from emails.
This email may contain a single flight or multiple flight segments.

Email Subject: ${subject}

Email Content:
${truncatedBody}

Extract flight information. Return ONLY a valid JSON object:

{
  "bookingType": "flight",
  "airline": "airline name",
  "confirmationNumber": "booking reference",
  "fromLocation": "departure city/airport (first leg)",
  "toLocation": "arrival city/airport (last leg)",
  "departureTime": "YYYY-MM-DDTHH:MM:SS",
  "arrivalTime": "YYYY-MM-DDTHH:MM:SS",
  "checkInDate": "YYYY-MM-DD (departure date)",
  "checkOutDate": "YYYY-MM-DD (return date or arrival date)",
  "totalPrice": numeric grand total only (no currency symbol/thousands separators),
  "currency": "USD/EUR/ILS/etc",
  "guestName": "passenger name",
  "city": "destination city",
  "country": "destination country",
  "segments": [
    {
      "airline": "airline name",
      "flightNumber": "flight number",
      "fromLocation": "departure city/airport",
      "toLocation": "arrival city/airport",
      "departureTime": "YYYY-MM-DDTHH:MM:SS",
      "arrivalTime": "YYYY-MM-DDTHH:MM:SS",
      "gate": "gate if available",
      "terminal": "terminal if available",
      "seat": "seat if available"
    }
  ],
  "vendor": "booking platform or airline",
  "confidence": 0.0 to 1.0
}

Rules:
- Handle Hebrew, English, and other languages
- Include ALL flight segments/legs
- All dates/times are LOCAL TO THE PLACE they happen — a departure of 10:15 from Luxembourg is 10:15 Luxembourg time. Copy the time exactly as printed in the email: never convert between timezones, and never append a timezone designator ("Z", "+02:00", "GMT") to the value
- For totalPrice, use the FINAL total paid for the whole booking — the "grand
  total" / "total" / "total paid" line that already includes all fares, taxes,
  and fees. Do NOT use an individual fare, tax, or fee line item, and do NOT add
  the line items up yourself; take the printed grand total even when it appears
  at the very bottom of a price breakdown table. Return a plain number with no
  currency symbol and "." as the decimal point. Watch locale formatting: "." is
  often a THOUSANDS separator (e.g. "1.250,00 EUR" means 1250.00).
- "city"/"country" = the trip DESTINATION, i.e. the arrival city/country of the
  FIRST outbound leg. For a round trip that returns to the origin, this is the
  turnaround (away-from-home) destination, NOT the origin/home country. Never
  leave "country" null when a destination airport/city is present.
${GeminiBookingParser.RECLASSIFY_NOTE}
- Return ONLY the JSON object`;
    }

    private buildCarRentalPrompt(subject: string, body: string): string {
        const truncatedBody = this.truncateBody(body);

        return `You are an AI that extracts car rental booking information from emails.

Email Subject: ${subject}

Email Content:
${truncatedBody}

Extract car rental information. Return ONLY a valid JSON object:

{
  "bookingType": "carRental",
  "company": "rental company name",
  "vehicleType": "vehicle type/category",
  "pickupLocation": "pickup location/address",
  "returnLocation": "return location/address",
  "pickupDate": "YYYY-MM-DDTHH:MM:SS",
  "returnDate": "YYYY-MM-DDTHH:MM:SS",
  "checkInDate": "YYYY-MM-DD (pickup date)",
  "checkOutDate": "YYYY-MM-DD (return date)",
  "confirmationNumber": "booking reference",
  "driverName": "driver name",
  "totalPrice": numeric value only,
  "currency": "USD/EUR/ILS/etc",
  "city": "pickup city",
  "country": "country",
  "vendor": "booking platform or rental company",
  "confidence": 0.0 to 1.0
}

Rules:
- Handle Hebrew, English, and other languages
- If a field is not found, use null
${GeminiBookingParser.RECLASSIFY_NOTE}
- Return ONLY the JSON object`;
    }

    private buildActivityPrompt(subject: string, body: string): string {
        const truncatedBody = this.truncateBody(body);

        return `You are an AI that extracts activity / attraction / tour / ticket booking information from emails.
This covers guided tours, museum or attraction admission tickets, theme parks, experiences, excursions, and event tickets.

Email Subject: ${subject}

Email Content:
${truncatedBody}

Extract the activity information. Return ONLY a valid JSON object (no markdown, no explanation):

{
  "bookingType": "activity",
  "activityName": "name of the activity/attraction/tour/ticket",
  "vendor": "provider or platform (GetYourGuide, Viator, Tiqets, Klook, the museum/operator name)",
  "propertyAddress": "meeting point or venue address",
  "city": "city name",
  "country": "country name",
  "checkInDate": "YYYY-MM-DDTHH:MM:SS (activity start / admission date-time)",
  "checkOutDate": "YYYY-MM-DDTHH:MM:SS (activity end if available)",
  "totalPrice": numeric value only,
  "currency": "USD/EUR/ILS/etc",
  "confirmationNumber": "booking confirmation number",
  "confidence": 0.0 to 1.0
}

Rules:
- Handle Hebrew, English, and other languages
- activityName is the most important field - always provide a best-effort value
- If a field is not found, use null
${GeminiBookingParser.RECLASSIFY_NOTE}
- Return ONLY the JSON object`;
    }

    private buildGenericPrompt(subject: string, body: string): string {
        const truncatedBody = this.truncateBody(body);

        return `You are an AI that inspects a forwarded email whose booking type is unclear.
Extract whatever travel/booking details you can so it can be shown to the user for manual review.

Email Subject: ${subject}

Email Content:
${truncatedBody}

Return ONLY a valid JSON object (no markdown, no explanation):

{
  "bookingType": "unknown",
  "detectedType": "one of: accommodation, flight, carRental, activity, unknown",
  "propertyName": "a short human-readable title for this item",
  "vendor": "provider or platform name if present",
  "confirmationNumber": "any confirmation/booking/reference number",
  "checkInDate": "YYYY-MM-DDTHH:MM:SS if any start/check-in/departure date is present",
  "checkOutDate": "YYYY-MM-DDTHH:MM:SS if any end/check-out/return date is present",
  "propertyAddress": "any venue/property/location address",
  "city": "city name",
  "country": "country name",
  "totalPrice": numeric value only,
  "currency": "USD/EUR/ILS/etc",
  "confidence": 0.0 to 1.0,
  "reasoning": "one sentence: what this email appears to be and why the type is or isn't clear"
}

Rules:
- Handle Hebrew, English, and other languages
- propertyName is the most important field - always provide a best-effort title
- If a field is not found, use null
- Return ONLY the JSON object`;
    }

    // ─── Normalize parsed data ───────────────────────────────────

    /** @param detectedType the type the caller settled on (keyword guess, or the model's correction). */
    private normalizeBookingData(parsed: any, detectedType: BookingType): ParsedBookingData {
        // The model's own verdict wins when it is mappable (it reads the whole
        // mail + attachment); the keyword-detected type is only a fallback.
        // See the "hotel"→flight bug: "hotel" used to miss the exact-match
        // check and get silently overridden by the flight keyword guess.
        let bookingType: BookingType;
        const declared = GeminiBookingParser.readDeclaredType(parsed);
        if (detectedType === BookingType.UNRECOGNIZED) {
            // The caller already resolved the type (e.g. the model rejected the
            // keyword guess); don't let a stale `bookingType` echo override it.
            bookingType = BookingType.UNRECOGNIZED;
        } else if (declared) {
            bookingType = declared;
        } else if (detectedType === BookingType.UNKNOWN) {
            bookingType = BookingType.UNRECOGNIZED;
        } else {
            bookingType = detectedType;
        }

        // Activities store their name in activityName; fold it into propertyName
        // so the shared fields carry it (mirrors the mobile app).
        if (!parsed.propertyName && parsed.activityName) {
            parsed.propertyName = parsed.activityName;
        }

        const result: ParsedBookingData = {
            bookingType,
            confidence: parsed.confidence ? parseFloat(parsed.confidence) : 0.7,
        };

        // Copy string fields
        const stringFields: (keyof ParsedBookingData)[] = [
            'propertyName', 'propertyAddress', 'city', 'country', 'airline',
            'currency', 'confirmationNumber', 'vendor', 'guestName',
            'flightNumber', 'fromLocation', 'toLocation',
            'vehicleType', 'pickupLocation', 'returnLocation', 'company', 'driverName',
        ];
        for (const field of stringFields) {
            if (parsed[field]) (result as any)[field] = parsed[field];
        }

        // Copy date fields as strings
        const dateFields: (keyof ParsedBookingData)[] = [
            'checkInDate', 'checkOutDate', 'departureTime', 'arrivalTime',
            'pickupDate', 'returnDate', 'cancellationDeadline',
        ];
        for (const field of dateFields) {
            if (parsed[field]) (result as any)[field] = parsed[field];
        }

        // Copy boolean fields
        if (parsed.freeCancellation !== undefined && parsed.freeCancellation !== null) {
            result.freeCancellation = Boolean(parsed.freeCancellation);
        } else if (parsed.cancellationDeadline) {
            // Infer freeCancellation from the presence of a cancellation deadline
            result.freeCancellation = true;
        }

        // Copy price — tolerate strings the model may emit despite the schema
        // (e.g. "1,367.22", "1367.22 EUR", "€1.367,22") so a valid total isn't
        // silently dropped.
        if (parsed.totalPrice !== undefined && parsed.totalPrice !== null) {
            const price = this.parsePrice(parsed.totalPrice);
            if (price !== null && price > 0) {
                result.totalPrice = price;
            }
        }

        // Copy flight segments
        if (parsed.segments && Array.isArray(parsed.segments)) {
            result.segments = parsed.segments;
        }

        return result;
    }

    /**
     * Parse a price that may arrive as a number or a messy string. Handles
     * currency symbols/codes, spaces, and both "1,234.56" (comma thousands) and
     * "1.234,56" (European) formats. Returns null when no number is found.
     */
    private parsePrice(value: unknown): number | null {
        if (typeof value === 'number') return isFinite(value) ? value : null;
        if (typeof value !== 'string') return null;

        // Strip everything except digits, separators and sign.
        let s = value.replace(/[^0-9.,-]/g, '').trim();
        if (!s) return null;

        const lastComma = s.lastIndexOf(',');
        const lastDot = s.lastIndexOf('.');
        if (lastComma > -1 && lastDot > -1) {
            // Whichever separator is last is the decimal separator.
            if (lastComma > lastDot) {
                // European "1.234,56" → drop dots (thousands), comma → dot.
                s = s.replace(/\./g, '').replace(',', '.');
            } else {
                // US "1,234.56" → drop commas (thousands).
                s = s.replace(/,/g, '');
            }
        } else if (lastComma > -1) {
            // Only commas: decimal if it looks like ",dd" at the end, else thousands.
            s = /,\d{1,2}$/.test(s) ? s.replace(',', '.') : s.replace(/,/g, '');
        }

        const n = parseFloat(s);
        return isFinite(n) ? n : null;
    }
}

export default GeminiBookingParser;
